import React, { useState, useContext, useEffect } from 'react'
import {
  SingleDatePicker,
  Checkbox
} from 'components/Form'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { Dialog, Popup } from 'components/Popup';
import API from 'lib/api';
import { getMessage } from 'lib/translator';
import Loader from 'components/Loader';
import { getNewDisplayAddress, updatedDeliveryOrderStatuses } from 'lib/commonlyused'
import { getSelectedStore } from 'lib/auth';
import { formatTime } from 'lib/datetime';
import exportFromJSON from 'export-from-json';
import { SplitContext } from 'containers/SplitContext'
import SPLIT_FEATURES from 'containers/SplitContext/features'
import B2BDeliveryNotesPDF from './B2BDeliveryNotesPDF'
import _ from 'lodash';
import moment from 'moment';

function BulkOperationFilters({ slotOptions }) {
  const splitConfig = useContext(SplitContext)
  const { splits } = splitConfig
  const zonePrefix = splits?.[SPLIT_FEATURES.ZONE_PREFIX_LABEL]
    ? JSON.parse(splits[SPLIT_FEATURES.ZONE_PREFIX_LABEL].config)
    : {}

  const [deliveryDate, setDeliveryDate] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [errorDialog, setErrorDialog] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [b2bDeliveryOrders, setB2bDeliveryOrders] = useState([])
  const [showB2BDeliveryNotePopup, setShowB2BDeliveryNotePopup] = useState(false);
  const [selectedOrderStatuses, setSelectedOrderStatuses] = useState(() => {
    return updatedDeliveryOrderStatuses.map(status => ({
      text: _.capitalize(status.split('_').pop()),
      value: status.split('_').pop(),
      isChecked: status === 'DELIVERY_ORDER_STATUS_PACKED' ||
        status === 'DELIVERY_ORDER_STATUS_DISPATCHED' ||
        status === 'DELIVERY_ORDER_STATUS_COMPLETED'
    }))
  })

  // Update selectedSlots when slotOptions changes
  useEffect(() => {
    if (slotOptions && slotOptions.length > 0) {
      const availableSlots = slotOptions.filter(opt => {
        const val = JSON.parse(opt.value);
        return val.startTime !== 'ALL';
      });

      setSelectedSlots(availableSlots.map(slot => ({
        ...slot,
        isChecked: true
      })));
    }
  }, [slotOptions]);

  const getSelectedOrderStatuses = () => {
    return selectedOrderStatuses.filter(x => x.isChecked).map(status => status.value)
  }

  const getSelectedSlots = () => {
    return selectedSlots.filter(x => x.isChecked).map(slot => JSON.parse(slot.value));
  }

  const fetchDeliveryOrders = async (listAction) => {
    const selectedStore = getSelectedStore();
    const queryAPI = new API({ url: `/order-service/v3/deliveryOrders` });
    const slotsToFetch = getSelectedSlots();

    const promises = slotsToFetch.map(slot => queryAPI.get({
      storeId: selectedStore.id,
      preferredDate: deliveryDate,
      status: getSelectedOrderStatuses(),
      slotFrom: slot.startTime,
      slotTo: slot.endTime,
      listAction
    }));

    const results = await Promise.all(promises);
    return { results, slotsToFetch };
  }

  const downloadCSV = async () => {
    try {
      setIsLoading(true);
      const { results, slotsToFetch } = await fetchDeliveryOrders('LIST_ACTION_FFS_DISPATCH');

      let allDeliveryOrders = [];
      results.forEach((result, index) => {
        if (result && result.deliveryOrders) {
          const ordersWithSlot = result.deliveryOrders.map(o => ({
            ...o,
            deliverySlot: o.deliverySlot || slotsToFetch[index]
          }));
          allDeliveryOrders = [...allDeliveryOrders, ...ordersWithSlot];
        }
      });

      if (allDeliveryOrders.length === 0) {
        setErrorDialog({
          title: 'Info',
          message: getMessage('order.filters.noOrderFound')
        })
      } else {
        // Determine filename based on selection
        let slotStartTime = 'MULTIPLE';
        let slotEndTime = 'MULTIPLE';

        // Check if all slots are selected (compare length with total available slots excluding ALL)
        const totalAvailableSlots = slotOptions.filter(opt => JSON.parse(opt.value).startTime !== 'ALL').length;

        if (slotsToFetch.length === 1) {
          slotStartTime = slotsToFetch[0].startTime.replace(/:/g, '').slice(0, -2);
          slotEndTime = slotsToFetch[0].endTime.replace(/:/g, '').slice(0, -2);
        } else if (slotsToFetch.length === totalAvailableSlots) {
          slotStartTime = 'ALL';
          slotEndTime = 'ALL';
        }

        const timestamp = moment().format('YYYY-MM-DDTHH_mm_ss');
        const fileName = `${deliveryDate}_${slotStartTime}_${slotEndTime}_${timestamp}`;
        const data = allDeliveryOrders.map(order => {
          const packageCounts = {};
          for (const key in order.packageCounts) {
            packageCounts[key] = order.packageCounts[key];
          }
          const address = getNewDisplayAddress(order.deliveryAddress, false, false);

          // remove white spaces infront of the address lines
          const formattedAddress = address.split('\n').map(x => x.trim()).join('\n');
          return {
            reference_number: order.referenceNumber,
            delivery_address: formattedAddress,
            postal_code: order.deliveryAddress.postalCode,
            time_slot: `${formatTime(order.deliverySlot.startTime)} - ${formatTime(order.deliverySlot.endTime)}`,
            ...packageCounts,
            early_deli: order.deliveryNotes?.deliveryPreference.earlyDelivery || '-',
            leave_at_door: order.deliveryNotes?.deliveryPreference.leaveAtDoor || '-',
            delivery_notes: order.deliveryNotes?.customerInstruction || '-',
          }
        })
        const sortedData = _.orderBy(data, ['postal_code'], ['asc']);
        exportFromJSON({ data: sortedData, fileName, exportType: exportFromJSON.types.csv });
      }
      setIsLoading(false);
    } catch (e) {
      console.log(e);
      setIsLoading(false);
      setErrorDialog({
        title: 'Error',
        message: getMessage('order.filters.unableDownloadCSV')
      })
    }
  }

  const downloadB2BDeliveryNotes = async () => {
    try {
      setIsLoading(true);
      const { results } = await fetchDeliveryOrders('LIST_ACTION_B2B');

      let allDeliveryOrders = [];
      results.forEach(result => {
        if (result && result.deliveryOrders) {
          allDeliveryOrders = [...allDeliveryOrders, ...result.deliveryOrders];
        }
      });

      if (allDeliveryOrders.length > 0) {
        setShowB2BDeliveryNotePopup(true)
        setB2bDeliveryOrders(allDeliveryOrders)
      } else {
        // Handle no orders case if needed, or just show empty popup as per original logic (which seems to only show if response.deliveryOrders exists)
        setShowB2BDeliveryNotePopup(true)
        setB2bDeliveryOrders([])
      }
    } catch (e) {
      console.log(e);
      setErrorDialog({
        title: 'Error',
        message: getMessage('order.filters.unableDownloadPDF')
      })
    } finally {
      setIsLoading(false)
    }
  }

  const reset = () => {
    setDeliveryDate(null);
    // Reset slots to all checked
    const availableSlots = slotOptions.filter(opt => {
      const val = JSON.parse(opt.value);
      return val.startTime !== 'ALL';
    });
    setSelectedSlots(availableSlots.map(slot => ({ ...slot, isChecked: false })));

    setSelectedOrderStatuses(data => data.map(status => ({
      ...status,
      isChecked: false
    })))
  }

  // ensure all filter fields are applied. !! cast the value to boolean instead of null/undefined/empty string
  const checkAllFiltersApplied = !!deliveryDate && selectedSlots.some(x => x.isChecked) && selectedOrderStatuses.some(x => x.isChecked)

  const getDeliverySlotName = () => {
    const slotsToFetch = getSelectedSlots();
    const totalAvailableSlots = slotOptions.filter(opt => JSON.parse(opt.value).startTime !== 'ALL').length;

    if (slotsToFetch.length === totalAvailableSlots) { return 'ALL'; }
    if (slotsToFetch.length === 1) {
      return `T${slotsToFetch[0].startTime.split(':')[0]}`
    }
    return 'MULTIPLE';
  }

  const handleUpdateItemChecked = (index) => {
    setSelectedOrderStatuses(data => data.map((item, i) =>
      i === index ? {
        ...item,
        isChecked: !item.isChecked
      } : item
    ))
  }

  const handleUpdateSlotChecked = (index) => {
    setSelectedSlots(data => data.map((item, i) =>
      i === index ? {
        ...item,
        isChecked: !item.isChecked
      } : item
    ))
  }

  return (
    <>
      <div className="form-fields">
        <SingleDatePicker
          label={getMessage('order.filters.deliveryDate.label')}
          placeholder={getMessage('order.filter.placedOn.placeholder')}
          name="bulk-operation-delivery-date"
          allowAllDates
          onChange={(date) => setDeliveryDate(date)}
          value={deliveryDate}
        />

        <div className="order-status-checkboxes-container" style={{ gridColumn: '1 / -1' }}>
          <p>{getMessage('order.filters.slot')}</p>
          <div className="order-status-checkboxes-wrapper">
            {
              selectedSlots.map((slot, index) =>
                <div key={slot.value}>
                  <Checkbox
                    name={`slot-${slot.value}`}
                    inlineLabel={slot.text}
                    value={slot.isChecked}
                    testid={`test-slot-${slot.value}`}
                    controlled
                    onChange={() => handleUpdateSlotChecked(index)}
                  />
                </div>
              )
            }
          </div>
        </div>

        <div className="order-status-checkboxes-container" style={{ gridColumn: '1 / -1' }}>
          <p>Select delivery order status</p>
          <div className="order-status-checkboxes-wrapper">
            {
              selectedOrderStatuses.map((status, index) =>
                <div key={status.value}>
                  <Checkbox
                    name={`status-${status.value}`}
                    inlineLabel={status.text}
                    value={status.isChecked}
                    testid={`test-${status.value}`}
                    controlled
                    onChange={() => handleUpdateItemChecked(index)}
                  />
                </div>
              )
            }
          </div>
        </div>

      </div>
      <button className={`button primary ${checkAllFiltersApplied ? '' : 'disabled'}`} onClick={downloadCSV}>
        {getMessage('order.filters.download.summary.text')}
      </button>
      <button className={`button primary ${checkAllFiltersApplied ? '' : 'disabled'}`} onClick={downloadB2BDeliveryNotes}>
        {getMessage('order.filters.download.b2b.delivery.notes')}
      </button>
      <button className='button secondary' onClick={reset}>
        {getMessage('deliveryOrder.bulkOperations.reset')}
      </button>
      {isLoading && <Loader />}
      <Popup
        heading={getMessage('order.filters.download.b2b.delivery.notes')}
        className="b2b-delivery-order-notes-popup"
        show={showB2BDeliveryNotePopup}
        close={() => setShowB2BDeliveryNotePopup(false)}
      >
        <div>
          {b2bDeliveryOrders.length === 0 ? getMessage('order.filters.download.b2b.noOrder') : `${b2bDeliveryOrders.length} order(s) found`}
          {
            b2bDeliveryOrders.length > 0 && (
              <div className='download-button-wrapper'>
                <PDFDownloadLink
                  document={
                    <B2BDeliveryNotesPDF
                      deliveryOrders={b2bDeliveryOrders}
                      zonePrefix={zonePrefix}
                      deliverySlotName={getDeliverySlotName()} />}
                  fileName={`${deliveryDate}_${getDeliverySlotName()}_${selectedOrderStatuses.map(x => x.text).join('_')}.pdf`}>
                  {({ _blob, _url, loading, _error }) =>
                    loading ? 'Generating pdf ...' : getMessage('order.filters.download.pdf')
                  }
                </PDFDownloadLink>
              </div>
            )
          }
        </div>
      </Popup>
      {errorDialog && (
        <Dialog
          show={!!errorDialog}
          title={errorDialog.title}
          information={errorDialog.message}
          close={() => setErrorDialog(null)}
          closeText={getMessage('dialog.okText')}
        />
      )}
    </>
  )
}

export default BulkOperationFilters;
